import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Drawer as Vaul } from "vaul";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "./dialog";

/**
 * Bottom sheet — replaces every full-screen modal from the RN app.
 * Built on Vaul (React drawer that respects mobile physics + a11y).
 *
 * NOTE: The workspace has two `@types/react` versions in the graph
 * (mobile pins ^19, website pins ^18). TypeScript's dedupe is
 * non-deterministic across machines, so vaul's `children` prop
 * occasionally resolves to a stricter `ReactNode` (no bigint) than our
 * own React 19 `ReactNode` — a real problem on CI, invisible locally.
 * We widen at the boundary by casting to Vaul's own component type,
 * which normalizes the mismatch without disabling any type-checking on
 * our own code above the boundary.
 */

// Vaul's own component signatures live behind a package boundary that
// re-resolves React types per install; treating the imported components
// as their loose-typed selves is the smallest, safest workaround.
/* eslint-disable @typescript-eslint/no-explicit-any */
const VaulRoot: any = Vaul.Root;
const VaulPortal: any = Vaul.Portal;
const VaulOverlay: any = Vaul.Overlay;
const VaulContent: any = Vaul.Content;
const VaulTitle: any = Vaul.Title;
const VaulDescription: any = Vaul.Description;
/* eslint-enable @typescript-eslint/no-explicit-any */

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /**
   * Form sheets opt in so a stray drag-down, backdrop tap or Escape
   * doesn't throw away what the user typed. Once any field inside the
   * sheet fires an input/change event the sheet counts as dirty: Vaul's
   * own dismiss gestures are switched off and those same gestures ask
   * "Discard changes?" instead. Closing from code (save, Cancel button)
   * is unaffected — it doesn't go through onOpenChange.
   */
  guardUnsaved?: boolean;
}

interface SheetGuard {
  dirty: boolean;
  markDirty: () => void;
  requestClose: () => void;
}

const SheetGuardContext = createContext<SheetGuard | null>(null);

export function Sheet({ open, onOpenChange, children, guardUnsaved = false }: SheetProps) {
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // A sheet that is re-opened starts clean.
  useEffect(() => {
    if (!open) setDirty(false);
  }, [open]);

  const guarded = guardUnsaved && dirty;
  const guard: SheetGuard = {
    dirty: guarded,
    markDirty: () => {
      if (guardUnsaved && !dirty) setDirty(true);
    },
    requestClose: () => (guarded ? setConfirmOpen(true) : onOpenChange(false)),
  };

  return (
    <SheetGuardContext.Provider value={guard}>
      <VaulRoot open={open} onOpenChange={onOpenChange} dismissible={!guarded}>
        {children}
      </VaulRoot>
      {guardUnsaved ? (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Discard changes?"
          description="What you've entered in this form will be lost."
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          destructive
          onConfirm={() => {
            setConfirmOpen(false);
            onOpenChange(false);
          }}
        />
      ) : null}
    </SheetGuardContext.Provider>
  );
}

export function SheetContent({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const guard = useContext(SheetGuardContext);
  // While dirty, Vaul ignores backdrop taps and Escape (dismissible is
  // off) — route them to the discard confirmation instead.
  const interceptDismiss = (e: Event) => {
    if (!guard?.dirty) return;
    e.preventDefault();
    guard.requestClose();
  };
  return (
    <VaulPortal>
      <VaulOverlay className="fixed inset-0 z-40 bg-[rgba(20,15,10,0.55)] backdrop-blur-sm" />
      <VaulContent
        onInputCapture={guard?.markDirty}
        onChangeCapture={guard?.markDirty}
        onPointerDownOutside={interceptDismiss}
        onEscapeKeyDown={interceptDismiss}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 flex flex-col bg-bg-elevated",
          // Full-bleed on mobile; capped and centered as a floating panel
          // on wider viewports so the sheet doesn't stretch edge-to-edge.
          // `inset-x-0` + `mx-auto` + `max-width` centers a fixed element
          // via auto margins — no transform, so it can't fight Vaul's own
          // drag transform on the Y axis.
          "sm:mx-auto sm:max-w-lg",
          "rounded-t-[var(--radius-xl)] shadow-warm-3",
          "max-h-[92vh] px-5 pb-6",
          className,
        )}
      >
        <SheetDragHandle />
        {children}
      </VaulContent>
    </VaulPortal>
  );
}

/**
 * Small pill at the top of the sheet content. Vaul already treats the
 * whole sheet as draggable, but the handle hints the gesture.
 *
 * On the first sheet render per session we run a subtle wiggle so a
 * first-time user notices the affordance. Session-guarded so we don't
 * annoy on the 20th open.
 */
function SheetDragHandle() {
  const guard = useContext(SheetGuardContext);
  // Vaul's drag is disabled while the sheet is dirty, so a pull on the
  // handle would otherwise do nothing. Treat a clear downward pull that
  // starts on the handle as a close request (→ discard confirmation).
  const pullStartY = useRef<number | null>(null);
  const [wiggle, setWiggle] = useState(false);
  useEffect(() => {
    try {
      const key = "sheet:nudged";
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
    } catch {
      /* private mode — fall through and always wiggle once per load */
    }
    setWiggle(true);
  }, []);

  return (
    <div
      aria-hidden
      className="-mx-5 flex justify-center pt-1.5 pb-3"
      style={{ touchAction: guard?.dirty ? "none" : undefined }}
      onPointerDown={(e) => {
        if (!guard?.dirty) return;
        pullStartY.current = e.clientY;
        // Keep receiving pointerup even once the finger leaves the handle.
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerUp={(e) => {
        const start = pullStartY.current;
        pullStartY.current = null;
        if (start !== null && e.clientY - start > 40) guard?.requestClose();
      }}
      onPointerCancel={() => {
        pullStartY.current = null;
      }}
    >
      <motion.div
        className="h-1 w-10 rounded-full bg-line-strong"
        animate={wiggle ? { rotate: [0, -6, 6, -4, 4, 0] } : undefined}
        transition={{ duration: 0.45, ease: "easeInOut" }}
      />
    </div>
  );
}

export function SheetHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="pb-2">
      <VaulTitle asChild>
        <h2
          className="font-display text-2xl leading-tight tracking-tight text-ink m-0"
          style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}
        >
          {title}
        </h2>
      </VaulTitle>
      {description ? (
        <VaulDescription asChild>
          <p className="mt-1 text-[13px] text-ink-subtle m-0">{description}</p>
        </VaulDescription>
      ) : (
        <VaulDescription className="sr-only">{title}</VaulDescription>
      )}
    </div>
  );
}

export function SheetBody({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex-1 overflow-y-auto py-2", className)}>{children}</div>
  );
}

export function SheetFooter({ children }: { children: ReactNode }) {
  return <div className="pt-3 flex flex-col gap-3">{children}</div>;
}
